import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  FormField,
  Header,
  Multiselect,
  MultiselectProps,
  Pagination,
  SpaceBetween,
  Table,
  TableProps
} from '@amzn/awsui-components-core';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { handleError, logger } from 'src/logger';
import { getMultiSelectPlaceHolderValue, getUniqueFieldValues } from 'src/utilities/CommonUtilities';
import { EmptyStateMessage } from '../EmptyStateMessage';
import { filtersLabel } from '../FinTechOpsCommonElements';
import {
  stringArrayToOptionsObj,
  optionsObjectToStringArray,
  returnFilterAgeSorted,
  returnSelectedOptionsAge
} from '../FinTechOpsDataProcessingFunctions';
import { TicketContextType, useTicketContext } from './TicketsContext';
import { StatusType } from './TicketsInterfaces';
import { TICKET_RESOLVED_DETAILS_COLUMN_DEFINITIONS, TICKET_RESOLVED_DETAILS_VISIBLE_CONTENT } from './TicketsTableFilterConfig';
import { DEFAULT_FILE_NAME, DEFAULT_SHEET_NAME, formatTableDataForExport } from 'src/utilities/CloudscapeTableUtils';
import { exportAsExcelFile } from 'src/utilities/FileServices';

interface DetailsTableProps {
  emptyMessage: string;
  filterProperties: any;
  visibleContent: readonly string[] | undefined;
  data: readonly any[];
  columnDefinitions: readonly TableProps.ColumnDefinition<any>[];
  pageSize?: number;
  headerName: string | undefined;
  stickyHeader?: boolean;
  tableLoadingText: string;
  detailsType: string;
  multiSelectPlaceholder: string[];
  rawDropDownValues: any[];
  statusType: StatusType;
  disabledValue: string;
  enableDownloadReport: true;
  downloadFileName: string;
}

/**
 * TicketDetailsResolvedTable Component
 * Displays a table with multi-select dropdown filters
 */
export const TicketDetailsResolvedTable: React.FC<DetailsTableProps> = (props) => {
  const { enableDownloadReport = false } = props;

  const ticketContext = useTicketContext();

  // State management
  const [preferences, setPreferences] = useState({
    pageSize: props.pageSize || 3,
    visibleContent: props.visibleContent
  });

  const [selectedOptionOne, setSelectedOptionOne] = useState<MultiselectProps.Options>([]);
  const [selectedOptionTwo, setSelectedOptionTwo] = useState<MultiselectProps.Options>([]);

  // Collection hook setup
  const { items, actions, collectionProps, paginationProps } = useCollection(props.data, {
    propertyFiltering: {
      filteringProperties: props.filterProperties,
      empty: <EmptyStateMessage title="No data" subtitle="No data to display." />,
      noMatch: (
        <EmptyStateMessage
          title="No matches"
          subtitle="No match found to display."
          action={<Button onClick={() => actions.setFiltering('')}>Clear Filter</Button>}
        />
      )
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {},
    selection: {}
  });

  /**
   * Updates ticket resolved details based on selected filters
   */
  const onTicketResolvedDetailsFilterBlur = useCallback(
    (ticketContext: TicketContextType, selectedOption: MultiselectProps.Options, resolutionTypeSelectedOption: MultiselectProps.Options) => {
      try {
        const resolutionTypeArray = optionsObjectToStringArray(resolutionTypeSelectedOption);
        const ticketAgeArray = returnSelectedOptionsAge(selectedOption);

        logger.info('Filtering resolved details with:', {
          resolutionTypes: resolutionTypeArray,
          ticketAges: ticketAgeArray
        });

        const filteredResolvedMonthData = ticketContext.filteredResolvedDetailsTable.filter((item) => {
          return ticketAgeArray.includes(item.resolved_date_UserTimeZone_formatted) && resolutionTypeArray.includes(item.resolution_type);
        });

        const parseData = {
          columnData: TICKET_RESOLVED_DETAILS_COLUMN_DEFINITIONS,
          visibleColumns: TICKET_RESOLVED_DETAILS_VISIBLE_CONTENT,
          rowData: filteredResolvedMonthData
        };

        ticketContext.setTicketResolvedDetailsTable(parseData);

        // Update filter options
        const uniqueFilteredResolvedMonthList = getUniqueFieldValues(filteredResolvedMonthData, 'resolved_date_UserTimeZone_formatted');
        const uniqueFilteredResolvedResolutionTypeList = getUniqueFieldValues(filteredResolvedMonthData, 'resolution_type');

        setSelectedOptionOne(stringArrayToOptionsObj(uniqueFilteredResolvedMonthList));
        setSelectedOptionTwo(stringArrayToOptionsObj(uniqueFilteredResolvedResolutionTypeList));

        logger.info('Successfully updated ticket resolved details', {
          filteredCount: filteredResolvedMonthData.length
        });
      } catch (error) {
        handleError('Error updating ticket resolved details:', error);
      }
    },
    [ticketContext]
  );

  /**
   * Returns appropriate onBlur event handler based on details type
   */
  const getOnBlurEvent = useCallback(
    (value: string) => {
      switch (value) {
        case 'ticketResolvedDetails':
          return onTicketResolvedDetailsFilterBlur(ticketContext, selectedOptionOne, selectedOptionTwo);
        default:
          handleError('Unknown details type:', value);
          return null;
      }
    },
    [ticketContext, selectedOptionOne, selectedOptionTwo, onTicketResolvedDetailsFilterBlur]
  );

  // Update filter options when context changes
  useEffect(() => {
    if (props.detailsType === 'ticketResolvedDetails') {
      try {
        setSelectedOptionOne(stringArrayToOptionsObj(ticketContext.filteredResolvedMonthList));
        setSelectedOptionTwo(stringArrayToOptionsObj(ticketContext.filteredResolvedResolutionTypeList));

        logger.info('Updated filter options', {
          monthListLength: ticketContext.filteredResolvedMonthList.length,
          resolutionTypeListLength: ticketContext.filteredResolvedResolutionTypeList.length
        });
      } catch (error) {
        handleError('Error updating filter options:', error);
      }
    }
  }, [ticketContext.filteredResolvedMonthList, ticketContext.filteredResolvedResolutionTypeList, props.detailsType]);

  /**
   * Handles the export functionality
   */
  const onDownloadClick = useCallback(() => {
    logger.info('Starting download process');
    try {
      const exportData = formatTableDataForExport(props.data, props.columnDefinitions);
      const fileName = props.downloadFileName ?? DEFAULT_FILE_NAME;
      exportAsExcelFile(exportData, fileName, DEFAULT_SHEET_NAME);
      logger.info('Download completed successfully');
    } catch (error: any) {
      logger.error('Error during download:', error);
    }
  }, [props.data, props.columnDefinitions, props.downloadFileName]);

  /**
   * Renders multi-select dropdowns based on rawDropDownValues
   */
  const renderMultiSelects = () => {
    const isDisabled = props.disabledValue === 'loading';

    if (props.rawDropDownValues.length === 0) {
      return null;
    }

    return props.rawDropDownValues.map((dropDownValue, index) => (
      <FormField key={index} label={filtersLabel(props.multiSelectPlaceholder[index])}>
        <Multiselect
          placeholder={getMultiSelectPlaceHolderValue(
            index === 0 ? selectedOptionOne : selectedOptionTwo,
            props.multiSelectPlaceholder[index],
            dropDownValue
          )}
          options={returnFilterAgeSorted(dropDownValue, `All ${props.multiSelectPlaceholder[index]}`)}
          selectedOptions={index === 0 ? selectedOptionOne : selectedOptionTwo}
          onChange={({ detail }) => {
            index === 0 ? setSelectedOptionOne(detail.selectedOptions) : setSelectedOptionTwo(detail.selectedOptions);
          }}
          onBlur={() => getOnBlurEvent(props.detailsType)}
          deselectAriaLabel={(e) => `Remove ${e.label}`}
          selectedAriaLabel="Selected"
          expandToViewport
          hideTokens
          virtualScroll
          filteringType="auto"
          loadingText={`Loading ${props.multiSelectPlaceholder[index]}`}
          errorText={`Unable to load ${props.multiSelectPlaceholder[index]}`}
          statusType={props.statusType}
          disabled={isDisabled}
        />
      </FormField>
    ));
  };

  /**
   * Renders the table header
   */
  const tableHeader = (
    <Header
      counter={`(${props.data.length})`}
      actions={
        <SpaceBetween direction="horizontal" size="m">
          {renderMultiSelects()}
          <Pagination {...paginationProps} />
          {enableDownloadReport && (
            <Button iconName="download" onClick={onDownloadClick} disabled={!props.data.length}>
              Download
            </Button>
          )}
        </SpaceBetween>
      }
    >
      {props.headerName}
    </Header>
  );

  return (
    <Table
      variant="embedded"
      {...collectionProps}
      columnDefinitions={props.columnDefinitions}
      items={items}
      loadingText={props.tableLoadingText}
      visibleColumns={props.visibleContent}
      resizableColumns={true}
      stickyHeader={props.stickyHeader ?? true}
      empty={<EmptyStateMessage title="No data" subtitle={props.emptyMessage} />}
      header={tableHeader}
    />
  );
};
